"use client";

import type * as React from "react";
import { useTranslations } from "next-intl";
import { Button, Key, Select, SelectValue } from "react-aria-components";
import { ItemBase, ListBox } from "@ui/components/core/ListBox";
import ChevronDown16 from "@ui/icons/16x16/chevron-down-16.svg";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";

const multiSelectTriggerVariants = cva(
  "tw-inline-flex tw-items-center tw-justify-between tw-whitespace-nowrap tw-rounded-[34px] tw-effect-color tw-disabled:cursor-not-allowed tw-gap-6 tw-transition-colors tw-outline-none focus-visible:tw-outline-focus",
  {
    variants: {
      size: {
        md: "h-40 f-subhead-05 ps-16 pe-12",
        lg: "h-48 f-subhead-05 ps-20 pe-16",
      },
      expand: {
        true: "w-full",
        false: "w-fit",
      },
      overlayStyle: {
        true: "bg-eggshell border tw-border-black/[0.08] text-black",
        false: "bg-eggshell border border-overlay-dark text-black",
      },
    },
    compoundVariants: [
      // Hover states
      {
        overlayStyle: true,
        className: "tw-enabled:hover:bg-black/[0.05] tw-hover:bg-black/[0.05]",
      },
      {
        overlayStyle: false,
        className: "hover:bg-overlay-lighter",
      },
    ],
    defaultVariants: {
      size: "md",
      expand: false,
      overlayStyle: false,
    },
  },
);

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  "aria-label": string;
  placeholder?: string;
  size?: "md" | "lg";
  expand?: boolean;
  className?: string;
  overlayStyle?: boolean;
  onChange?: (value: Key[]) => void;
}

/**
 * TODO: We need to refactor this to be a generic select component with multiple selection support.
 * Probably we will deprecate the existing Select component that is based off Radix-ui Select component.
 */
export function MultiSelect({
  options,
  "aria-label": ariaLabel,
  placeholder,
  size = "md",
  expand = false,
  className,
  overlayStyle,
  onChange,
}: MultiSelectProps) {
  const t = useTranslations("ClientKeys.Globals.select");
  const defaultPlaceholder = placeholder || t("default_placeholder");

  return (
    <Select selectionMode='multiple' aria-label={ariaLabel} onChange={onChange}>
      <Button
        className={cn(
          multiSelectTriggerVariants({
            size,
            expand,
            overlayStyle,
          }),
          "pressed:bg-overlay-lighter",
          className,
        )}
      >
        <SelectValue className='tw-flex tw-items-center tw-gap-8 tw-truncate'>
          {({ selectedItems }: { selectedItems: (ItemBase | null)[] }) => {
            if (selectedItems.length === 0) {
              return <span>{defaultPlaceholder}</span>;
            }

            if (selectedItems.length === 1) {
              return <span className='tw-truncate'>{selectedItems[0]?.textValue}</span>;
            }

            return <span className='tw-truncate'>{t("selected_count", { count: selectedItems.length })}</span>;
          }}
        </SelectValue>

        <div className='tw-relative tw-flex tw-shrink-0 tw-items-center tw-justify-center'>
          <ChevronDown16 className='text-lightest' />
        </div>
      </Button>

      <ListBox placement='bottom end' items={options.map(option => ({ id: option.value, textValue: option.label }))} />
    </Select>
  );
}
